import {animate, style, transition, trigger} from '@angular/animations';
import {ChangeDetectionStrategy, Component, HostBinding, input, output, signal} from '@angular/core';
import {OphCheckboxComponent} from '../../../shared/design/oph-checkbox/oph-checkbox.component';
import {OphXButtonComponent} from '../../../shared/design/oph-x-button/oph-x-button.component';

export interface CheckboxOption {
  text: string;
  isChecked: boolean;
  isDisabled?: boolean;
  isInput?: boolean;
  inputIndex?: number;
  uid?: string;
}

@Component({
  selector: 'cb-checkbox-option',
  standalone: true,
  imports: [OphCheckboxComponent, OphXButtonComponent],
  template: `
    <div
      class="option-row"
      [class.checked]="option().isChecked"
      [class.disabled]="option().isDisabled"
      [class.multiline]="option().isInput"
      [class.focused]="isFocused()"
      [class.error]="hasError()"
    >
      <oph-checkbox
        color="blue"
        [checked]="option().isChecked"
        [width]="25"
        textOrientation="right"
        [text]="''"
        (valueChange)="onToggle($event)"
      ></oph-checkbox>
      @if (option().isInput) {
        <textarea
          class="option-textarea"
          [class.focused]="isFocused()"
          [placeholder]="option().text"
          [value]="inputValue()"
          (input)="onInputChange($event)"
          (focus)="onTextareaFocus()"
          (blur)="onInputBlur()"
          rows="1"
        ></textarea>
      } @else {
        <span class="option-text">{{ option().text }}</span>
      }
      @if (removable() && isFocused()) {
        <oph-x-button [width]="16" (mousedown)="$event.preventDefault()" (buttonClick)="remove.emit()"></oph-x-button>
      }
    </div>
  `,
  styles: [
    `
      :host {
        display: block;
      }

      oph-checkbox {
        flex-shrink: 0;
      }

      oph-x-button {
        flex-shrink: 0;
      }

      .option-row {
        display: flex;
        align-items: center;
        gap: 31px;
        min-height: 45px;
        padding: 5px 9px;
        border: 1px solid #e5e3dc;
        border-radius: 10px;
        background: transparent;
        transition: all 0.2s ease;

        &.checked {
          background: #fefdf5;
        }

        &.disabled {
          opacity: 0.4;
          pointer-events: none;
        }

        &.multiline {
          padding-top: 10px;
          padding-bottom: 10px;
        }

        &.focused {
          border-color: #0f8898;
        }

        &.error {
          border-color: #e74c3c;
        }
      }

      .option-textarea {
        flex: 1;
        border: none;
        background: transparent;
        font-family: 'Asap', sans-serif;
        font-weight: 400;
        font-size: 16px;
        line-height: 24px;
        color: #4a443d;
        outline: none;
        resize: none;
        overflow: hidden;
        padding: 0;
        margin: 0;
        field-sizing: content;
        max-height: 48px;
        transition: max-height 0.2s ease;

        &::placeholder {
          color: #a6a39b;
        }

        &.focused {
          max-height: none;
        }
      }

      .option-text {
        font-family: 'Asap', sans-serif;
        font-size: 16px;
        font-weight: 400;
        line-height: 24px;
        color: #4a443d;
        display: -webkit-box;
        -webkit-line-clamp: 2;
        -webkit-box-orient: vertical;
        overflow: hidden;
      }
    `,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
  animations: [
    trigger('slideInFade', [
      transition(':enter', [
        style({opacity: 0, height: 0, overflow: 'hidden'}),
        animate('200ms ease-out', style({opacity: 1, height: '*'})),
      ]),
      transition(':leave', [
        style({overflow: 'hidden'}),
        animate('200ms ease-in', style({opacity: 0, height: 0, overflow: 'hidden'})),
      ]),
    ]),
  ],
})
export class CbCheckboxOptionComponent {
  @HostBinding('@slideInFade') readonly slideInFadeTrigger = '';

  option = input.required<CheckboxOption>();
  inputValue = input<string>('');
  hasError = input(false);
  removable = input(false);

  isFocused = signal(false);

  valueChange = output<boolean>();
  inputChange = output<string>();
  inputFocus = output<void>();
  remove = output<void>();

  onToggle(checked: boolean): void {
    this.valueChange.emit(checked);
  }

  onInputChange(event: Event): void {
    const target = event.target as HTMLTextAreaElement;
    this.inputChange.emit(target.value);
  }

  onTextareaFocus(): void {
    this.isFocused.set(true);
    this.inputFocus.emit();
  }

  onInputBlur(): void {
    this.isFocused.set(false);
  }
}
